/**************************************************************************************************/
/*   File: /src/app/stores/reducers/authorization/reducer.ts                                      */
/*   Project: newplan-frontend                                                                    */
/*   Created Date: Friday February 22nd 2019                                                      */
/*   Author: Andrea Zamboni andrea.zamboni@trilogis.it                                            */
/*   -----                                                                                        */
/*   Last Modified: Tue May 14 2019                                                               */
/*   Modified By: Andrea Zamboni                                                                  */
/*   -----                                                                                        */
/*   Copyright (c) 2019 Trilogis s.r.l. for SIAG                                                  */
/**************************************************************************************************/
import * as _ from 'lodash';

import * as fromShared from '../../../shared';
import * as fromActions from '../../actions';
import * as fromStates from '../../states';

/**
 * the auth state reducer
 * @param state The state
 * @param action The action
 */
export function authorizationReducer(
  state: fromStates.fromAuthorization.AuthorizationState = fromStates.fromAuthorization.INITIAL_AUTH_STATE,
  action: fromActions.fromAuthorization.AuthActions |
    fromActions.fromAuthorization.FileActions |
    fromActions.fromMessages.MessagesActions |
    fromActions.fromUi.Actions):
  fromStates.fromAuthorization.AuthorizationState {

  switch (action.type) {
    // Reducer for Request authorization 
    case fromActions.fromAuthorization.AuthorizationActionTypes.CHECK_AUTHORIZATION_STATUS:
      return {
        ...state,
        isLoading: true
      };
    // Reducer for request authorization success (User authorized on newplan backend)
    case fromActions.fromAuthorization.AuthorizationActionTypes.CHECK_AUTHORIZATION_STATUS_SUCCESS:


      switch (action.payload.authorizationStatus.status) {
        case fromShared.enums.AuthorizationStatus.NOT_EXISTENT:
          return {
            ...state,
            isLoading: false,
            isAuthorized: false,
            isAuthorizing: false,
            isRevoked: false,
            isRejected: false,
            files: [],
            messages: [
              new fromShared.models.Message(
                fromShared.enums.MessageType.INFO,
                fromShared.enums.Messages.AUTHORIZE_REQUEST,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME,
                true
              )
            ]
          };
        case fromShared.enums.AuthorizationStatus.REQUESTED:
          return {
            ...state,
            isLoading: false,
            isAuthorized: false,
            isAuthorizing: true,
            isRevoked: false,
            isRejected: false,
            messages: [
              new fromShared.models.Message(
                fromShared.enums.MessageType.INFO,
                fromShared.enums.Messages.AUTHORIZE_PENDING,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME,
                true
              )
            ]
          };
        case fromShared.enums.AuthorizationStatus.APPROVED:
          return {
            ...state,
            messages: state.isAuthorized ? [] : [
              new fromShared.models.Message(
                fromShared.enums.MessageType.SUCCESS,
                fromShared.enums.Messages.AUTHORIZE_ACCEPTED,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME,
                true
              )
            ],
            isLoading: false,
            isAuthorized: true,
            isAuthorizing: false,
            isRevoked: false,
            isRejected: false,
          };
        case fromShared.enums.AuthorizationStatus.REJECTED:
          return {
            ...state,
            isLoading: false,
            isAuthorized: false,
            isAuthorizing: false,
            isRevoked: false,
            isRejected: true,
            files: [],
            uploadFinished: false,
            messages: [
              new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.AUTHORIZE_REJECTED,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME,
                true
              )
            ]
          };
        case fromShared.enums.AuthorizationStatus.REVOKED:
          return {
            ...state,
            isLoading: false,
            isAuthorized: false,
            isAuthorizing: false,
            isRevoked: true,
            isRejected: false,
            files: [],
            uploadFinished: false,
            messages: [
              new fromShared.models.Message(
                fromShared.enums.MessageType.ERROR,
                fromShared.enums.Messages.AUTHORIZE_REVOKE,
                fromShared.enums.MessagePositionEnum.TOP,
                fromStates.fromAuthorization.STATE_NAME,
                true
              )
            ]
          };
        default:
          return {
            ...state
          };
      }

    // Reducer for request authorizazion fail when user is not pending and not authorized
    case fromActions.fromAuthorization.AuthorizationActionTypes.CHECK_AUTHORIZATION_STATUS_FAIL:
      return {
        ...state,
        messages: [
          ...state.messages,
          // action.payload.message
        ],
        isLoading: false
      };
    // Reducer for request authorization send to newplan backend ot access to functionalities
    case fromActions.fromAuthorization.AuthorizationActionTypes.SEND_AUTHORIZATION:
      return {
        ...state,
        isLoading: true
      };
    // Reducer for action success of send request
    case fromActions.fromAuthorization.AuthorizationActionTypes.SEND_AUTHORIZATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthorizing: true,
        files: [],
        messages: [
          action.payload.message
        ]
      };
    // Reducer for action fail of send request
    case fromActions.fromAuthorization.AuthorizationActionTypes.SEND_AUTHORIZATION_FAIL:
      return {
        ...state,
        isLoading: false,
        isAuthorizing: false,
        files: [],
        messages: [
          ...state.messages,
          action.payload.message
        ]
      };
    // Reducer for message removed
    case fromActions.fromMessages.MessagesActionsType.REMOVE_MESSAGE_ACTION:
      return {
        ...state,
        messages: [...state.messages.filter(x => x !== action.payload.message)]
      };
    // Reducer for notify message
    case fromActions.fromMessages.MessagesActionsType.NOTIFY_MESSAGE:
      return action.payload.message.stateName === fromStates.fromAuthorization.STATE_NAME ?
        {
          ...state,
          messages: [
            action.payload.message
          ]
        } : { ...state };
    // Reducer for action file added: It copy object getting file id and saving it into state.
    case fromActions.fromAuthorization.FileActionTypes.FILE_ADDED:
      return {
        ...state,
        files: [
          ...state.files,
          ...action.payload.files.map(
            (file: fromShared.models.FileModel) => {
              return {
                id: file.id,
                isUploading: file.isUploading,
                isUploaded: file.isUploaded,
                buffer: _.merge({}, file.buffer),
                rawData: new Blob([file.buffer], { type: file.buffer.type })
              };
            }
          )
        ]
      };
    // Reducer for action file removed fom collection in state
    case fromActions.fromAuthorization.FileActionTypes.FILE_REMOVED:
      return {
        ...state,
        files: [
          ...state.files.map(
            (file: fromShared.models.FileModel) => {
              return {
                id: file.id,
                isUploading: true,
                isUploaded: false,
                buffer: _.merge({}, file.buffer),
                rawData: new Blob([file.buffer], { type: file.buffer.type })
              };
            }
          )
        ]
      };
    // Reducer for action file removed success fom collection in state
    case fromActions.fromAuthorization.FileActionTypes.FILE_REMOVED_SUCCESS:
      return {
        ...state,
        files: [
          ...state.files.filter(file => {
            return file.id !== action.payload.id;
          })
        ],
        uploadFinished: state.files.length > 0 ?
          state.files.map(
            file => file.isUploaded
          ).reduce(
            (accumulator, currentValue) => accumulator && currentValue
          )
          : false
      };
    // Refucer for action file removed fail
    case fromActions.fromAuthorization.FileActionTypes.FILE_REMOVED_FAIL:
      return {
        ...state,
        files: [],
        uploadFinished: false,
        messages: [
          action.payload.message
        ]
      };
    // Reducer for file clear action
    case fromActions.fromAuthorization.FileActionTypes.FILE_CLEAR:
      return {
        ...state,
        files: []
      };

    case fromActions.fromAuthorization.FileActionTypes.FILE_UPLOAD:
      return {
        ...state,
        files: [
          ...state.files.map(
            file => {
              if (file.id === action.payload.file.id) {
                file.isUploading = true;
              }
              return file;
            }
          )
        ],
        uploadFinished: state.files.length > 0 ?
          state.files.map(
            file => file.isUploaded
          ).reduce(
            (accumulator, currentValue) => accumulator && currentValue
          )
          : false
      };
    case fromActions.fromAuthorization.FileActionTypes.FILE_UPLOAD_SUCCESS:
      return {
        ...state,
        files: [
          ...state.files.map(
            file => {
              if (file.id === action.payload.file.id) {
                file.id = action.payload.documentId.id;
                file.isUploading = false;
                file.isUploaded = true;
              }
              return file;
            }
          )
        ],
        uploadFinished: state.files.length > 0 ?
          state.files.map(
            file => file.isUploaded
          ).reduce(
            (accumulator, currentValue) => accumulator && currentValue
          )
          : false
      };
    case fromActions.fromAuthorization.FileActionTypes.FILE_UPLOAD_FAILURE:
      return {
        ...state,
        files: [],
        uploadFinished: true
      };

    case fromActions.fromUi.UiActionTypes.UNSET_LOGGED_USER:
      return {
        ...fromStates.fromAuthorization.INITIAL_AUTH_STATE
      };

    default:
      return state;
  }
}

// Helpers
/**
 * The get isLoading helper function
 * @param state The state
 */
const getIsLoading = (state: fromStates.fromAuthorization.AuthorizationState) => state.isLoading;
/**
 * The get is isAuthorized helper function
 * @param state The state
 */
const getIsAuthorized = (state: fromStates.fromAuthorization.AuthorizationState) => state.isAuthorized;
/**
 * The get is is authorizing helper function
 * @param state The state
 */
const getIsAuthorizing = (state: fromStates.fromAuthorization.AuthorizationState) => state.isAuthorizing;
/**
 * The get is revoked helper
 * @param state The state
 */
const getIsRevoked = (state: fromStates.fromAuthorization.AuthorizationState) => state.isRevoked;
/**
 * The get is rejected helper
 * @param state The state
 */
const getIsRejected = (state: fromStates.fromAuthorization.AuthorizationState) => state.isRejected;
/**
 * The getFiles helper function
 * @param state The state
 */
const getFiles = (state: fromStates.fromAuthorization.AuthorizationState) => state.files;
/**
 * The getUploadFinished helper function
 * @param state The state
 */
const getUploadFinished = (state: fromStates.fromAuthorization.AuthorizationState) => state.uploadFinished;
/**
 * The get messages helper function
 * @param state The state
 */
const getMessages = (state: fromStates.fromAuthorization.AuthorizationState) => state.messages;

/**
 * Export the helpers
 */
export const helpers = {
  getIsLoading,
  getIsAuthorized,
  getIsAuthorizing,
  getIsRevoked,
  getIsRejected,
  getFiles,
  getUploadFinished,
  getMessages
};
